import { useEffect, useState } from 'react'

import { usePlaceAutocomplete, usePlaceDetails } from 'queries/crm-place-search'
import useAppSetting from 'hooks/use-app-setting'
import { useGooglePlacesAutocomplete } from 'queries/place-search'

import { Toast } from 'components'

import { DropdownItem } from 'components/Dropdown/types'
import { CustomAddressConfiguration } from 'store/types/settings'
import { Prediction } from 'services/place-search'

import { filteredOptions, processPredictions } from './helper'
import { AddressPayload } from 'services/lead-address'
import { BaseAddress } from 'types/global'

type Porps = {
  value?: string
  searchSessionToken: string
  isEnabled?: boolean
  defaultOptions?: DropdownItem<string>[]
  countryFallback: string
  addressConfigs: CustomAddressConfiguration[]
  isDebounced?: boolean
  isPressEnter?: boolean
}

const mapAddress = (address: AddressPayload): BaseAddress => ({
  province: address.province,
  city: address.city,
  formatted: address.address_label,
  addressLine1: address.address_line_1,
  addressLine2: address.address_line_2,
  country: address.country,
  lat: address.lat || null,
  lng: address.lng || null,
  suburb: address.suburb,
  postalCode: address.postal_code,
  ward: address.ward || null,
})

export function usePlaceData({
  searchSessionToken,
  value = '',
  isEnabled = true,
  defaultOptions,
  countryFallback,
  addressConfigs,
  isDebounced = true,
  isPressEnter = false,
}: Porps) {
  const {
    key: { USE_GOOGLE_PLACE_API },
  } = useAppSetting()

  const [options, setOptions] = useState<DropdownItem<string>[]>([])

  // CRM setup
  const { getPlaceAutoComplete, isLoading, isError } = usePlaceAutocomplete()
  const { getPlaceDetails } = usePlaceDetails()

  // Google API setup
  const {
    handleSearchWithGoogleAPI,
    isServiceReady,
    error,
    isLoading: isGoogleApiPlaceLoading,
    handlePlaceDetailsWithGoogleAPI,
  } = useGooglePlacesAutocomplete({
    apiKey: process.env.GOOGLE_MAPS_KEY || '',
    libraries: ['places'],
    countryFallback,
    addressConfigs,
  })

  // ===================== Details Handlers =====================
  const handlePlaceDetailsWithCRMAPI = async ({ rawPlace }: { rawPlace: Prediction }) => {
    const { data: placeDetails, isSuccess, isError } = await getPlaceDetails({
      placeId: rawPlace.place_id,
    })
    if (isSuccess) {
      const mapped = mapAddress(placeDetails.mapped_address)

      return {
        address: {
          ...mapped,
          lat: placeDetails?.lat,
          lng: placeDetails?.lng,
          formatted: rawPlace.formatted_address || rawPlace?.description,
        },
      }
    }

    if (isError) {
      Toast.toastMessage({ variant: 'error', message: 'unable_to_get_place_details_error' })
      return {}
    }
  }

  const handlePlaceDetail = async ({ rawPlace }: { rawPlace: Prediction }) => {
    if (USE_GOOGLE_PLACE_API) {
      if (!isServiceReady) {
        Toast.toastMessage({ message: 'google_service_not_ready_error', variant: 'error' })
        return
      }

      return await handlePlaceDetailsWithGoogleAPI(rawPlace)
    }

    return await handlePlaceDetailsWithCRMAPI({ rawPlace })
  }

  // ===================== Autocomplete Handlers =====================
  const handleWithGoogleAPI = async (value?: string) => {
    if (!isServiceReady) {
      Toast.toastMessage({ message: 'google_service_not_ready_error', variant: 'error' })
      setOptions([])
      return
    }

    if (value == '' || !value) {
      setOptions([])
      return
    }

    const res = await handleSearchWithGoogleAPI(value)
    if (res) {
      setOptions(res)
      return
    }
    if (error) {
      console.error(error)
      Toast.toastMessage({ message: error.message, variant: 'error' })
      setOptions([])
      return
    }
  }

  const handleWithCRMPlaceAPI = async (inputValue: string) => {
    if (inputValue === '') {
      setOptions([])
      return
    }

    const res = await getPlaceAutoComplete({
      input: inputValue,
      sessiontoken: searchSessionToken,
    })

    if (res.isSuccess) {
      setOptions(processPredictions(res.data))
      return
    }

    if (res.isError) {
      Toast.toastMessage({ message: res.error.message, variant: 'error' })
      setOptions([])
      return
    }

    setOptions([])
  }

  // ===================== Debounced Search Effect =====================
  useEffect(() => {
    if (isDebounced === true && isEnabled === true) {
      if (value == '') {
        setOptions([])
        return
      }

      // search on the default options first
      const op = filteredOptions(value, defaultOptions)

      if (op && op?.length > 0) {
        setOptions(op)
        return
      }

      if (USE_GOOGLE_PLACE_API) {
        handleWithGoogleAPI(value)
        return
      }

      handleWithCRMPlaceAPI(value)
      return
    }
  }, [value, isDebounced, isEnabled, defaultOptions, USE_GOOGLE_PLACE_API])

  // ===================== Search on Enter =====================
  const handlePlaceSearchOnEnterKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isPressEnter === true && isEnabled === true) {
      e.preventDefault()

      const inputEl = e.currentTarget.querySelector('input')
      const inputValue = inputEl?.value

      if (inputValue === '' || !inputValue) {
        setOptions([])
        return
      }

      if (inputValue) {
        // search on the default options first
        const op = filteredOptions(inputValue, defaultOptions)

        if (op && op?.length > 0) {
          setOptions(op)
          return
        }

        if (USE_GOOGLE_PLACE_API) {
          handleWithGoogleAPI(inputValue)
          return
        }

        handleWithCRMPlaceAPI(inputValue)
        return
      }
    }
  }

  return {
    options,
    isLoading: isLoading || isGoogleApiPlaceLoading,
    isError,
    handlePlaceSearchOnEnterKeyPress,
    handlePlaceDetail,
  }
}
